/**
 * Kit when-clause matching + scoreFromKit.
 * Position/fire orchestration stays in raid-kit-interp.
 */
import { raidCatalog, type RaidClassId } from "../../data/raid-catalog";
import type { RaidBoss, RaidUnit } from "./raid-types";
import { underPressure as underPressureHit } from "../../sim";
import {
  type EngagementStyle,
  hostileAttackReach,
  hostileHardMinSep,
  classKnobs,
  engagementStyle,
  fleeFromAdds,
} from "./raid-engage";
import { spaceOutBossGoal } from "./raid-motion";
import { dispositionKnob, dispositionOf } from "./raid-disposition";
import {
  collectRaidDangerZones,
  raidEngageSoftSpace,
  softThreatWantsSpace,
} from "./raid-threat";
import type { SpawnHeatState } from "./raid-spawn-heat";
import { unitIsStaffHealer } from "./judgment/hero-registry";

type CombatTable = (typeof raidCatalog.balance)["combat"];

export const kitCombatTable = (): CombatTable => raidCatalog.balance.combat;

export type KitDecideCtx = {
  u: RaidUnit;
  boss: RaidBoss;
  allies: readonly RaidUnit[];
  enemies: readonly RaidUnit[];
  focusBoss: boolean;
  time: number;
  /** When false, engage never aims the dormant boss. */
  includeBoss?: boolean;
  spawnHeat?: SpawnHeatState | null;
};

export type KitRuntime = {
  measureStandMul: (u: RaidUnit) => number;
  measureFleeMul: (u: RaidUnit) => number;
  /** Direct HP% survive threshold from grit (`surviveHpThr`). */
  gritSurviveThr: (u: RaidUnit) => number;
  bondHealMul: (u: RaidUnit) => number;
  /** Bond → heal slot split among healers (0=pile lowest). */
  bondHealSplitMul: (u: RaidUnit) => number;
};

export const defaultKitPorts: KitRuntime = {
  measureStandMul: (u) => dispositionKnob(dispositionOf(u), "standRangeMul"),
  measureFleeMul: (u) => dispositionKnob(dispositionOf(u), "fleeDistMul"),
  gritSurviveThr: (u) => dispositionKnob(dispositionOf(u), "surviveHpThr"),
  bondHealMul: (_u) => 1,
  bondHealSplitMul: (u) => dispositionKnob(dispositionOf(u), "healSplitMul"),
};

export type WhenClause = {
  always?: boolean;
  addInWeaponRange?: boolean;
  /** Sticky engage target already inside weapon/boss reach (wantAttack viable). */
  engageInReach?: boolean;
  targetIsBoss?: boolean;
  notTargetIsBoss?: boolean;
  knob?: string;
  /** HP% below grit `surviveHpThr` (direct thr; no class base×mul). */
  hpBelowSurviveThr?: boolean;
  engagementIs?: EngagementStyle;
  orFleeFromAdds?: boolean;
  notFleeFromAdds?: boolean;
  /** Local hostile density — table pack.countThreshold. */
  packPressureAbove?: boolean;
  /** Feet inside any combat-armed enemy melee disk. */
  inEnemyMelee?: boolean;
  /**
   * Feet inside Soft melee **or** enemy AA (+pad). Thrall Soft≪AA — tanks
   * must space on hit-reach packs, not Soft disk alone.
   */
  nearEnemyHit?: boolean;
  /** Feet inside thrall (`defId=add`) AA — pack space without brute-only false opens. */
  /** @deprecated Prefer packChewInAa */
  thrallInAa?: boolean;
  packChewInAa?: boolean;
  /**
   * SoftThreat intensity above threshold. `true` → table `engageSoftSpace`;
   * number → literal intensity cutoff.
   */
  softThreatAbove?: boolean | number;
  /** Adjutant boss-meat tank — skip pack chase (holdMeleeGap instead). */
  notBossAnchor?: boolean;
  isBossAnchor?: boolean;
};

type KitScoreBlock = {
  knob?: string;
  requiresSpaceGoal?: boolean;
  value: number;
  hpBelowSurviveThr?: boolean;
  requiresHealerAlive?: boolean;
  underPressureSec?: number;
  underPressureSecCombatKey?: string;
};

type KitScoreShape = {
  role: RaidClassId;
  score?: KitScoreBlock;
  position?: {
    minDistMode?: "kiteRing";
    minDistCombatKey?: string;
    onlyWhenCloser?: boolean;
  };
};

export function kitById(kitId: string): KitScoreShape & Record<string, unknown> {
  const kits = raidCatalog.behaviorKits.kits as Record<
    string,
    KitScoreShape & Record<string, unknown>
  >;
  const kit = kits[kitId];
  if (!kit) throw new Error(`Unknown AI behavior kit: ${kitId}`);
  return kit;
}

export function kitCombatNum(combat: CombatTable, key: string): number {
  const v = (combat as unknown as Record<string, unknown>)[key];
  if (typeof v !== "number") {
    throw new Error(`Missing combat key for kit: ${key}`);
  }
  return v;
}

export function kitKnobOn(role: RaidClassId, key: string): boolean {
  return !!(classKnobs(role) as Record<string, unknown>)[key];
}

export function whenMatches(
  when: WhenClause,
  args: {
    addInWeaponRange: boolean;
    engageInReach: boolean;
    targetIsBoss: boolean;
    packPressure: boolean;
    inEnemyMelee: boolean;
    nearEnemyHit: boolean;
    thrallInAa: boolean;
    packChewInAa?: boolean;
    softIntensity: number;
    isBossAnchor: boolean;
    u: RaidUnit;
    role: RaidClassId;
    combat: CombatTable;
    rt: KitRuntime;
    enemies: readonly RaidUnit[];
    dangerZones?: ReturnType<typeof collectRaidDangerZones>;
  },
): boolean {
  if (when.always) return true;
  if (when.softThreatAbove != null) {
    const thr =
      when.softThreatAbove === true
        ? raidEngageSoftSpace()
        : Number(when.softThreatAbove);
    if (
      !softThreatWantsSpace({
        u: args.u,
        softIntensity: args.softIntensity,
        enterThr: thr,
        enemies: args.enemies,
        dangerZones: args.dangerZones,
      })
    ) {
      return false;
    }
  }
  if (when.packPressureAbove === true && !args.packPressure) return false;
  if (when.packPressureAbove === false && args.packPressure) return false;
  if (when.inEnemyMelee === true && !args.inEnemyMelee) return false;
  if (when.inEnemyMelee === false && args.inEnemyMelee) return false;
  if (when.nearEnemyHit === true && !args.nearEnemyHit) return false;
  if (when.nearEnemyHit === false && args.nearEnemyHit) return false;
  const packChewInAa = args.packChewInAa ?? args.thrallInAa;
  if (when.packChewInAa === true && !packChewInAa) return false;
  if (when.packChewInAa === false && packChewInAa) return false;
  if (when.thrallInAa === true && !packChewInAa) return false;
  if (when.thrallInAa === false && packChewInAa) return false;
  if (when.notBossAnchor === true && args.isBossAnchor) return false;
  if (when.isBossAnchor === true && !args.isBossAnchor) return false;
  if (
    when.addInWeaponRange != null &&
    when.addInWeaponRange !== args.addInWeaponRange
  ) {
    return false;
  }
  if (when.engageInReach != null && when.engageInReach !== args.engageInReach) {
    return false;
  }
  if (when.targetIsBoss != null && when.targetIsBoss !== args.targetIsBoss) {
    return false;
  }
  if (when.notTargetIsBoss === true && args.targetIsBoss) return false;
  if (when.knob) {
    if (!kitKnobOn(args.role, when.knob)) return false;
  }
  if (when.hpBelowSurviveThr) {
    const thr = args.rt.gritSurviveThr(args.u);
    if (!(thr > 0) || !(args.u.hp / args.u.maxHp < thr)) return false;
  }
  if (when.engagementIs != null && !when.orFleeFromAdds) {
    if (engagementStyle(args.role) !== when.engagementIs) return false;
  }
  if (when.notFleeFromAdds === true && fleeFromAdds(args.role)) {
    return false;
  }
  if (when.orFleeFromAdds) {
    const engOk =
      when.engagementIs != null &&
      engagementStyle(args.role) === when.engagementIs;
    if (when.engagementIs != null) {
      const fleeOk = fleeFromAdds(args.role);
      if (!(engOk || fleeOk)) return false;
    } else if (!fleeFromAdds(args.role)) {
      return false;
    }
  }
  return true;
}

/** Score a kit (space / tank critical). Unknown score shape → 0. */
export function scoreFromKit(
  kitId: string,
  ctx: KitDecideCtx,
  rt: KitRuntime,
): number {
  const kit = kitById(kitId);
  const score = kit.score;
  if (!score) return 0;
  const role = kit.role;

  const combatForScore = kitCombatTable();
  if (score.hpBelowSurviveThr) {
    const hpPct = ctx.u.maxHp > 0 ? ctx.u.hp / ctx.u.maxHp : 1;
    const thr = rt.gritSurviveThr(ctx.u);
    if (!(thr > 0) || !(hpPct < thr)) return 0;
  }
  if (score.requiresHealerAlive) {
    const healerAlive = ctx.allies.some((a) => unitIsStaffHealer(a));
    if (!healerAlive) return 0;
  }
  {
    const pressureSec = score.underPressureSecCombatKey
      ? kitCombatNum(combatForScore, score.underPressureSecCombatKey)
      : score.underPressureSec;
    if (pressureSec != null) {
      if (!underPressureHit(ctx.u.hit, ctx.time, pressureSec)) return 0;
    }
  }
  if (score.knob && !kitKnobOn(role, score.knob)) return 0;
  if (score.requiresSpaceGoal && kit.position) {
    const combat = kitCombatTable();
    const bossReach = hostileAttackReach(ctx.u, ctx.boss);
    const hardMin = hostileHardMinSep(ctx.u, ctx.boss);
    const epsRaw = (combat as { bodySepReachEps?: number }).bodySepReachEps;
    const reachEps = typeof epsRaw === "number" ? epsRaw : 2;
    const engageRing = Math.min(
      Math.max(bossReach * 0.92, hardMin),
      Math.max(hardMin, bossReach - reachEps),
    );
    const pos = kit.position;
    let minDist: number;
    if (pos.minDistCombatKey) {
      minDist = kitCombatNum(combat, pos.minDistCombatKey);
    } else if (pos.minDistMode === "kiteRing") {
      minDist = Math.min(
        Math.max(combat.dpsKiteDist, combat.dpsFleeBossDist + 10),
        engageRing,
      );
    } else {
      return 0;
    }
    const goal = spaceOutBossGoal(ctx.u, ctx.boss, ctx.allies, minDist, {
      onlyWhenCloser: pos.onlyWhenCloser !== false,
      time: ctx.time,
    });
    if (!goal) return 0;
  }
  return score.value;
}
